import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

/**
 * The app's homepage, from this side of the glass.
 *
 * Two halves. The labels decide what the shelves are called — "Featured" can
 * become "Sponsored" the day a slot is actually sold, without a release. The
 * shelf itself decides what is on them.
 *
 * Anything on the Featured shelf is shown ONLY there: a reader who also follows
 * that tournament sees it under Featured, not twice. Turn the shelf off and its
 * contents fall back into everyone's Following section.
 */

interface AppSetting { key: string; value: string | null }

interface Feature {
  id: number;
  entity_type: 'tournament' | 'user' | 'team' | 'match';
  entity_id: number;
  entity_name: string;
  sort_order: number;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  live_now: boolean;
}

interface SearchHit { id: number; label: string; hint: string }

const SETTING_KEYS = [
  'homepage.featured.label',
  'homepage.featured.enabled',
  'homepage.following.label',
  'homepage.discover.label',
  'homepage.discover.enabled',
  'homepage.promo.enabled',
  'homepage.promo.payload',
];

const ENTITY_TYPES: Array<{ value: Feature['entity_type']; label: string; hint: string }> = [
  { value: 'tournament', label: 'Tournament', hint: 'Every match in it' },
  { value: 'user', label: 'Person', hint: 'Every match they play or officiate' },
  { value: 'team', label: 'Team', hint: 'Every match they are in' },
  { value: 'match', label: 'Match', hint: 'Just this one' },
];

/** `datetime-local` wants `YYYY-MM-DDTHH:mm`, and an ISO string has more than that. */
const toLocalInput = (iso: string | null): string => (iso ? iso.slice(0, 16) : '');

const Toggle: React.FC<{ on: boolean; onClick: () => void }> = ({ on, onClick }) => (
  <button
    onClick={onClick}
    className={`relative w-11 h-6 rounded-full transition-colors ${on ? 'bg-primary-600' : 'bg-gray-300'}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${on ? 'translate-x-5' : ''}`}
    />
  </button>
);

const Homepage: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // ADM-2: Save settings stays off until the live values have loaded once.
  const [loaded, setLoaded] = useState(false);

  // Add form
  const [addType, setAddType] = useState<Feature['entity_type']>('tournament');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [idInput, setIdInput] = useState('');

  const promo = (() => {
    try {
      return JSON.parse(values['homepage.promo.payload'] || '{}') || {};
    } catch {
      return {};
    }
  })();

  const setV = (k: string, v: string) => setValues((p) => ({ ...p, [k]: v }));
  const setPromo = (k: string, v: string) =>
    setV('homepage.promo.payload', JSON.stringify({ ...promo, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, featuresRes]: any[] = await Promise.all([
        api.get('/app-settings'),
        api.get('/homepage-features'),
      ]);
      const map: Record<string, string> = {};
      (settingsRes.data || []).forEach((r: AppSetting) => { map[r.key] = r.value || ''; });
      setValues(map);
      setFeatures(featuresRes.data || []);
      setLoaded(true);
    } catch {
      toast.error('Failed to load the homepage settings. Saving is off until they load; reload the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Searching on every keystroke would hammer the API for a field nobody types
  // slowly in, so wait until they stop.
  useEffect(() => {
    if (query.trim().length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await api.get('/homepage-features/search', {
          params: { type: addType, q: query.trim() },
        });
        setHits(res.data || []);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, addType]);

  const saveSettings = async () => {
    if (!loaded) { toast.error('The live settings have not loaded, so nothing was saved. Reload the page and try again.'); return; }
    setSaving(true);
    try {
      const settings: Record<string, string> = {};
      SETTING_KEYS.forEach((k) => { settings[k] = values[k] ?? ''; });
      await api.put('/app-settings', { settings });
      toast.success('Homepage settings updated');
      load();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /// Search and the id box both end up here. The server checks the id exists
  /// and says so if it does not, so a typo is caught rather than parked on the
  /// shelf pointing at nothing.
  const add = async (entityId: number, label: string) => {
    setAdding(true);
    try {
      await api.post('/homepage-features', {
        entity_type: addType,
        entity_id: entityId,
        sort_order: features.length,
      });
      toast.success(`${label} added to the shelf`);
      setQuery('');
      setHits([]);
      setIdInput('');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not add that');
    } finally {
      setAdding(false);
    }
  };

  const addById = () => {
    const id = Number(idInput.trim());
    if (!Number.isInteger(id) || id <= 0) {
      toast.error('Enter a numeric id');
      return;
    }
    add(id, `${addType} ${id}`);
  };

  const patch = async (id: number, body: Record<string, unknown>) => {
    try {
      await api.put(`/homepage-features/${id}`, body);
      load();
    } catch {
      toast.error('Could not update that entry');
    }
  };

  const remove = async (row: Feature) => {
    if (!window.confirm(`Remove "${row.entity_name}" from the Featured shelf?`)) return;
    try {
      await api.delete(`/homepage-features/${row.id}`);
      toast.success('Removed');
      load();
    } catch {
      toast.error('Could not remove that entry');
    }
  };

  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= features.length) return;
    const a = features[index];
    const b = features[target];
    await Promise.all([
      api.put(`/homepage-features/${a.id}`, { sort_order: b.sort_order }),
      api.put(`/homepage-features/${b.id}`, { sort_order: a.sort_order }),
    ]);
    load();
  };

  const featuredOn = values['homepage.featured.enabled'] === 'true';

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Homepage</h1>
        <p className="text-sm text-gray-500">
          What the app shows on its home screen, and what is on the Featured shelf.
        </p>
      </div>

      {loading ? <ListLoader /> : (
        <div className="space-y-6 max-w-4xl">

          {/* ── Shelf names ─────────────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 space-y-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Shelf names</h2>
              <p className="text-xs text-gray-400">
                Shown as headings in the app. Change &quot;Featured&quot; to &quot;Sponsored&quot; when a
                slot is actually sold.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">Show the Featured shelf</div>
                <div className="text-xs text-gray-400">
                  Off means the whole shelf disappears, and everything on it goes back to showing in
                  people&apos;s Following section.
                </div>
              </div>
              <Toggle
                on={featuredOn}
                onClick={() => setV('homepage.featured.enabled', featuredOn ? 'false' : 'true')}
              />
            </div>

            <div className="flex items-center justify-between border-t border-gray-100 pt-5">
              <div>
                <div className="text-sm font-medium text-gray-800">Show the Discover shelf</div>
                <div className="text-xs text-gray-400">
                  Cricket near the reader. On means it always shows; off means never — it is not
                  tied to whether they follow anyone.
                </div>
              </div>
              <Toggle
                on={values['homepage.discover.enabled'] !== 'false'}
                onClick={() =>
                  setV(
                    'homepage.discover.enabled',
                    values['homepage.discover.enabled'] === 'false' ? 'true' : 'false',
                  )
                }
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-gray-100 pt-5">
              {[
                { key: 'homepage.featured.label', label: 'Featured heading', ph: 'Featured' },
                { key: 'homepage.following.label', label: 'Following heading', ph: 'Following' },
                { key: 'homepage.discover.label', label: 'Discover heading', ph: 'Happening near you' },
              ].map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <input
                    type="text"
                    value={values[f.key] || ''}
                    placeholder={f.ph}
                    onChange={(e) => setV(f.key, e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-400">
              Someone who follows nobody sees a prompt to follow people above Discover. Turning
              Discover off leaves them with just that prompt and whatever is Featured.
            </div>
          </div>

          {/* ── Promo strip ─────────────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">Promo strip</div>
                <div className="text-xs text-gray-400">The banner at the bottom of the home screen.</div>
              </div>
              <Toggle
                on={values['homepage.promo.enabled'] === 'true'}
                onClick={() =>
                  setV('homepage.promo.enabled', values['homepage.promo.enabled'] === 'true' ? 'false' : 'true')
                }
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Image or animation URL</label>
                <input
                  type="text"
                  value={promo.media_url || ''}
                  onChange={(e) => setPromo('media_url', e.target.value)}
                  placeholder="https://.../banner.png or .json (Lottie)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Opens on tap</label>
                <input
                  type="text"
                  value={promo.link_url || ''}
                  onChange={(e) => setPromo('link_url', e.target.value)}
                  placeholder="https://... (leave empty for no link)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving || !loaded}
              className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>

          {/* ── The shelf ───────────────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 space-y-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Featured shelf</h2>
              <p className="text-xs text-gray-400">
                Saved as you go — these do not need the Save button above. A tournament brings all of its
                matches; a person brings everything they play or officiate.
              </p>
            </div>

            {!featuredOn && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                The shelf is switched off, so none of this is showing in the app right now.
              </div>
            )}

            {/* Add */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {ENTITY_TYPES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => { setAddType(t.value); setQuery(''); setHits([]); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      addType === t.value
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
                <span className="text-xs text-gray-400 self-center ml-1">
                  {ENTITY_TYPES.find((t) => t.value === addType)?.hint}
                </span>
              </div>

              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${addType}s by name...`}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />

              {/* Search finds things by name; this is for when the name is
                  ambiguous, or the thing has none worth typing — an untitled
                  match, a player with a very common name. */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Or add by id
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={idInput}
                    onChange={(e) => setIdInput(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') addById(); }}
                    placeholder={`${addType} id, e.g. 41`}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <button
                  onClick={addById}
                  disabled={adding || idInput.trim().length === 0}
                  className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60"
                >
                  Add
                </button>
              </div>

              {searching && <div className="text-xs text-gray-400">Searching...</div>}
              {!searching && query.trim().length >= 2 && hits.length === 0 && (
                <div className="text-xs text-gray-400">Nothing matched.</div>
              )}
              {hits.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y max-h-64 overflow-auto">
                  {hits.map((hit) => (
                    <button
                      key={hit.id}
                      disabled={adding}
                      onClick={() => add(hit.id, hit.label)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:opacity-60"
                    >
                      <div className="text-sm text-gray-800">{hit.label}</div>
                      <div className="text-xs text-gray-400">
                        {hit.hint ? `${hit.hint} · ` : ''}id {hit.id}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* List */}
            {features.length === 0 ? (
              <div className="text-sm text-gray-400 py-4 text-center">
                Nothing on the shelf yet. The app shows no Featured section until something is.
              </div>
            ) : (
              <div className="divide-y border border-gray-200 rounded-lg">
                {features.map((row, index) => (
                  <div key={row.id} className="p-3 sm:p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{row.entity_name}</div>
                        <div className="text-xs text-gray-400">
                          {ENTITY_TYPES.find((t) => t.value === row.entity_type)?.label} · id {row.entity_id}
                          {row.live_now
                            ? <span className="ml-2 text-green-600">showing now</span>
                            : <span className="ml-2 text-gray-400">not showing</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => move(index, -1)} disabled={index === 0}
                          className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40">↑</button>
                        <button onClick={() => move(index, 1)} disabled={index === features.length - 1}
                          className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40">↓</button>
                        <Toggle on={row.is_active} onClick={() => patch(row.id, { is_active: !row.is_active })} />
                        <button onClick={() => remove(row)}
                          className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Starts</label>
                        <input
                          type="datetime-local"
                          value={toLocalInput(row.starts_at)}
                          onChange={(e) => patch(row.id, { starts_at: e.target.value || null })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Ends</label>
                        <input
                          type="datetime-local"
                          value={toLocalInput(row.ends_at)}
                          onChange={(e) => patch(row.id, { ends_at: e.target.value || null })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      Leave both empty to keep it up until someone takes it down. An end date is the easy way
                      to stop a sponsored slot expiring only when somebody remembers.
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;
